import React, { useEffect, useState, useMemo } from "react";
import AdvisorMainContent from "../layout/AdvisorMain";
import Card1 from "./AdvisorCard";
import { useDispatch, useSelector } from "react-redux";
import { getFilteredCalls } from "../store/Slice/DashboardSlice";
import FilterDropdown from "../components/FilterDropdown/FilterDropdown";
import SortByDropdown from "../components/SortByDropdown/SortByDropdown";

import SkeletonCard from "../components/SkeletonCard";

const AdvisorDashboard = () => {
  const dispatch = useDispatch();
  const { filteredData, loading } = useSelector((state) => state.dashboard);
  const [filters, setFilters] = useState({
    status: [],
    segment: [],
  });
  const [sortBy, setSortBy] = useState("newest_first");

  const [archivedCalls, setArchivedCalls] = useState(() => {
    try {
      const saved = localStorage.getItem("archivedCalls");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Error loading archived calls:", error);
      return [];
    }
  });

  useEffect(() => {
    dispatch(
      getFilteredCalls({
        filters: null,
        sortBy: null,
        endpoint: "calls/filter",
      })
    );

    // Set up polling every 5 minutes
    const refreshInterval = setInterval(() => {
      console.log("Auto-refreshing advisor dashboard data...");
      dispatch(
        getFilteredCalls({
          filters: null,
          sortBy: null,
          endpoint: "calls/filter",
        })
      );
    }, 300000);

    return () => clearInterval(refreshInterval);
  }, [dispatch]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    console.log(`selected sort: ${value}`);
  };

  const handleApplyFilters = (appliedFilters = filters) => {
    console.log("Applied filters:", appliedFilters);
    const apiFilters = {};
    if (appliedFilters.status && appliedFilters.status.length > 0) {
      apiFilters.status = appliedFilters.status;
    }
    if (appliedFilters.segment && appliedFilters.segment.length > 0) {
      apiFilters.segment = appliedFilters.segment;
    }

    dispatch(
      getFilteredCalls({
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : null,
        sortBy,
        endpoint: "calls/filter",
      })
    );
  };

  const handleApplySort = (appliedSortBy = sortBy) => {
    console.log("Applied sort:", appliedSortBy);

    // Apply sorting by calling API with current sort and filters
    const apiFilters = {};
    if (filters.status && filters.status.length > 0) {
      apiFilters.status = filters.status;
    }
    if (filters.segment && filters.segment.length > 0) {
      apiFilters.segment = filters.segment;
    }

    dispatch(
      getFilteredCalls({
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : null,
        sortBy: appliedSortBy,
        endpoint: "calls/filter",
      })
    );
  };

  const handleArchiveCall = (callId) => {
    setArchivedCalls((prev) => {
      const updated = [...prev, callId];

      localStorage.setItem("archivedCalls", JSON.stringify(updated));
      console.log("Archived call ID:", callId);
      console.log("Updated archived calls array:", updated);
      console.log(
        "Saved to localStorage:",
        localStorage.getItem("archivedCalls")
      );
      return updated;
    });
  };

  const getDaysRemaining = (call) => {
    if (!call.createdAt || !call.duration) return null;
    const createdDate = new Date(call.createdAt);
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + call.duration);
    const today = new Date();
    const daysRemaining = Math.ceil(
      (expiryDate - today) / (1000 * 60 * 60 * 24)
    );
    return daysRemaining;
  };

  const expiringSoonCalls = useMemo(() => {
    return (filteredData || [])
      .filter((call) => {
        if (archivedCalls.includes(call.id)) return false;
        const daysRemaining = getDaysRemaining(call);
        return (
          daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 2
        );
      })
      .sort((a, b) => {
        const daysA = getDaysRemaining(a);
        const daysB = getDaysRemaining(b);
        return daysA - daysB;
      });
  }, [filteredData, archivedCalls]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = (filteredData || []).filter((call) => {
      if (archivedCalls.includes(call.id)) return false;
      const daysRemaining = getDaysRemaining(call);
      const isExpiringSoon =
        daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 2;
      return !isExpiringSoon;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "confidence_high_to_low":
          return b.confidence - a.confidence;
        case "confidence_low_to_high":
          return a.confidence - b.confidence;
        case "target_price_high_to_low":
          return b.targetPrice - a.targetPrice;
        case "target_price_low_to_high":
          return a.targetPrice - b.targetPrice;
        case "duration_high_to_low":
          return b.duration - a.duration;
        case "duration_low_to_high":
          return a.duration - b.duration;
        case "newest_first":
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case "oldest_first":
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [filteredData, archivedCalls, sortBy]);

  return (
    <AdvisorMainContent >
      <div className="max-w-[1600px] mx-auto w-full flex flex-col min-h-full">
        <div className="px-2 py-6 lg:px-4 xl:px-6 flex-1">
          <div className="mb-6 bg-white shadow-sm  border-b border-t border-brand-border">
            <div className="flex items-center justify-start p-4 lg:p-6 flex-row gap-4">
              <h2 className="text-xl lg:text-[24px] xl:text-[24px] font-bold text-[#454545]">
                Dashboard
              </h2>
              <div className="flex gap-3 w-auto justify-start flex-row items-center">
                <FilterDropdown
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onApply={handleApplyFilters}
                  sortBy={sortBy}
                  isMobile={false}
                />
                <SortByDropdown
                  sortBy={sortBy}
                  onSortChange={handleSortChange}
                  onApply={handleApplySort}
                  isMobile={false}
                />
              </div>
            </div>
          </div>

          {!loading && expiringSoonCalls.length > 0 && (
            <div className="mb-8">
              <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    className="w-5 h-5 text-orange-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h3 className="text-lg lg:text-xl font-semibold text-orange-800">
                    Expiring Soon
                  </h3>
                </div>
                <p className="text-sm text-orange-700">
                  {expiringSoonCalls.length} call
                  {expiringSoonCalls.length !== 1 ? "s" : ""} expiring within 2
                  days
                </p>
              </div>
              <div className="flex flex-col gap-4 lg:gap-5 w-full">
                {expiringSoonCalls.map((call) => {
                  const daysRemaining = getDaysRemaining(call);
                  return (
                    <Card1
                      key={`expiring-${call.id}`}
                      data={call}
                      onArchive={handleArchiveCall}
                      isArchived={false}
                      daysRemaining={daysRemaining}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-4">
            <h3 className="text-lg lg:text-xl font-semibold text-[#454545]">
              Active Calls
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {filteredAndSortedData.length} active call
              {filteredAndSortedData.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex flex-col gap-4 lg:gap-5 w-full pb-6">
            {loading ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : filteredAndSortedData.length > 0 ? (
              filteredAndSortedData.map((call) => (
                <Card1
                  key={call.id}
                  data={call}
                  onArchive={handleArchiveCall}
                  isArchived={false}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No active calls found</p>
              </div>
            )}
          </div>
        </div>

        <footer className="text-center py-4 lg:py-5 px-4 bg-[#f0f2f5] border-t border-[#e5e5e5] mt-8 text-sm lg:text-base">
          <p className="text-[#666]">
            Need help? Contact our{" "}
            <a
              href="tel:+919398319624"
              className="text-primary font-medium hover:underline"
            >
              Customer Care team
            </a>
            .
          </p>
        </footer>
      </div>
    </AdvisorMainContent>
  );
};

export default AdvisorDashboard;
